/**
 * API Configuration and Credentials
 * Centralized configuration for all API endpoints, credentials, and authentication
 * Uses environment variables for secure credential management
 */

import { ENV_CONFIG } from './envConfig';

// Environment-based configuration
const getEnvironment = (): 'development' | 'production' => {
  return ENV_CONFIG.IS_PRODUCTION ? 'production' : 'development';
};

// API Endpoints Configuration
export const API_ENDPOINTS = {
  // OneAI API Configuration (matches Vite proxy paths)
  ONEAI: {
    BASE_URL: '',  // Empty for relative paths
    CHAT_COMPLETION: '/api/oneai/chat/completions',
    OAUTH_TOKEN: '/api/oauth/token'
  },
  
  // Direct API Configuration (production)
  DIRECT: {
    BASE_URL: ENV_CONFIG.API.BASE_URL,
    CHAT_COMPLETION: '/OneAI/OneAssist/v1/chat/completions',
    OAUTH_TOKEN: '/oauth/token'
  },
  
  // External APIs (if any)
  EXTERNAL: {
    // Add external API endpoints here if needed
  }
} as const;

// Authentication Configuration
export const AUTH_CONFIG = {
  // OAuth Configuration - from environment variables
  OAUTH: {
    CLIENT_ID: ENV_CONFIG.OAUTH.CLIENT_ID,
    CLIENT_SECRET: ENV_CONFIG.OAUTH.CLIENT_SECRET,
    GRANT_TYPE: ENV_CONFIG.OAUTH.GRANT_TYPE as 'client_credentials',
    SCOPE: 'read write'
  },
  
  // Default Headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  
  // OneAI Specific Headers - from environment variables
  ONEAI_HEADERS: {
    USER_ID: ENV_CONFIG.ONEAI.USER_ID,
    USERNAME: ENV_CONFIG.ONEAI.USERNAME,
    FORMAT_ID: ENV_CONFIG.ONEAI.FORMAT_ID
  }
} as const;

// API Request Configuration
export const API_CONFIG = {
  // Request Timeouts - from environment variables
  TIMEOUTS: {
    DEFAULT: ENV_CONFIG.API.TIMEOUT, // From env var
    OAUTH: 10000,   // 10 seconds
    CHAT: 60000     // 60 seconds for chat requests
  },
  
  // Retry Configuration - from environment variables
  RETRY: {
    MAX_ATTEMPTS: ENV_CONFIG.API.RETRY_ATTEMPTS, // From env var
    DELAY_MS: 1000,
    BACKOFF_MULTIPLIER: 2
  },
  
  // Request Limits
  LIMITS: {
    MAX_TOKENS: 50,
    TEMPERATURE: 0.7,
    MAX_MESSAGE_LENGTH: 4000
  }
} as const;

// Environment-specific configuration
export const getApiConfig = (customBaseUrl?: string, useProxy?: boolean) => {
  const environment = getEnvironment();
  const isProduction = environment === 'production';
  
  // In development, use Vite proxy (relative paths)
  // In production, use direct API calls or custom base URL
  const shouldUseProxy = useProxy ?? !isProduction;
  
  if (shouldUseProxy) {
    // Development mode - use Vite proxy with relative paths
    return {
      baseURL: '', // Empty for relative paths through Vite proxy
      oneaiEndpoint: API_ENDPOINTS.ONEAI.CHAT_COMPLETION,
      oauthEndpoint: API_ENDPOINTS.ONEAI.OAUTH_TOKEN,
      timeout: API_CONFIG.TIMEOUTS.DEFAULT
    };
  }
  
  // Production mode - use direct API calls
  return {
    baseURL: customBaseUrl || API_ENDPOINTS.DIRECT.BASE_URL,
    oneaiEndpoint: API_ENDPOINTS.DIRECT.CHAT_COMPLETION,
    oauthEndpoint: API_ENDPOINTS.DIRECT.OAUTH_TOKEN,
    timeout: API_CONFIG.TIMEOUTS.DEFAULT
  };
};

// Security utilities
export const generateBasicAuthHeader = (): string => {
  const credentials = `${AUTH_CONFIG.OAUTH.CLIENT_ID}:${AUTH_CONFIG.OAUTH.CLIENT_SECRET}`;
  return `Basic ${btoa(credentials)}`;
};

// Session utilities
export const generateSessionId = (): string => {
  return `${Date.now()}${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
};

// UUID generation for trace IDs
// export const generateUUIDHex = (): string => {
//   return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
//     const r = Math.random() * 16 | 0;
//     const v = c === 'x' ? r : (r & 0x3 | 0x8);
//     return v.toString(16);
//   });
// };

export const generateUUIDHex = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  }).replace(/-/g, ''); // Remove all dashes to get pure hex string
};

// Export types for TypeScript
export type Environment = ReturnType<typeof getEnvironment>;
export type ApiEndpoints = typeof API_ENDPOINTS;
export type AuthConfig = typeof AUTH_CONFIG;
export type ApiConfigType = typeof API_CONFIG;
